import React from "react";
import CloseIcon from "../../assets/img/common/close-gray-icon.jpg";
import { useRef } from "react";
import useOutsideClick from "../../hooks/useOutsideClick";
import { Cropper } from "react-cropper";
import { createRef } from "react";
import { useEffect } from "react";
import "cropperjs/dist/cropper.css";
import { ReactComponent as ProfileEdtiIcon } from "../../assets/img/common/profile-edit-icon.svg";
import handleCropImgChange from "../../utils/handleCropImgChange";
import notify from "./notify";
import { Bars } from "react-loader-spinner";
import { ACCEPT_IMAGE_FILES } from "../../Constants";
import useApi from "../../hooks/useApi";

// Modal component for cropping and uploading images

const CropImageModal = ({
  show, // Flag to display/hide the modal
  setShow, // Function to control the visibility of the modal
  cropImage, // State variable for cropped image data
  setCropImage, // Function to set cropped image data
  setImage, // Function to set the original image
  image, // Original image data
  type = "avatar", // Type of image being cropped (default: "avatar")
  id, // ID related to the image (if applicable)
  formik, // Formik instance for form validation and manipulation
}) => {
  const modalRef = useRef(null);

  // Custom hook to close the modal when clicking outside it
  useOutsideClick(modalRef, () => setShow(false), null, show);

  // Reference for the Cropper component
  const cropperRef = createRef(null);

  // Event handler for changing the input file
  const onChange = (e) => {
    handleCropImgChange(e, setImage);
  };

  // Function to get cropped image data
  const getCropData = () => {
    let cropper = cropperRef?.current?.cropper;
    if (typeof cropper !== "undefined" && cropper?.getCroppedCanvas()) {
      // Logic to determine image type
      let url = cropper?.url;
      let type;
      if (url?.startsWith("http")) {
        const match = url?.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
        const foundedType = match ? match[1] : "png";
        type = `image/${foundedType}`;
      } else if (url?.startsWith("data:image/")) {
        const foundedType = url?.match(/^data:image\/([A-Za-z]+);/)[1];
        type = `image/${foundedType}`;
      } else {
        throw new Error("Invalid image URL provided");
      }
      return new Promise((resolve, reject) => {
        cropper.getCroppedCanvas().toBlob(
          (blob) => {
            if (blob) {
              let ext = type?.split("/")[1];
              resolve(new File([blob], `img.${ext}`, { type }));
            } else {
              reject("Failed to convert canvas to blob");
            }
          },
          type,
          0.7
        );
      });
    } else {
      notify("Invalid image type!", "error");
      return null;
    }
  };

  // Custom hook for API requests
  const { loading: uploadImgLoading, PUBLIC_REQUEST } = useApi();

  // Function to handle image upload
  const handleUploadImage = async () => {
    const formData = new FormData();

    // Callbacks for API request success and error
    const onSuccess = async (data) => {
      setCropImage(data?.link);
      if (formik) formik.setFieldValue(id, data?.link);
      await formik?.validateField(id);
      notify(data?.message, "success");
      setShow(false);
    };
    const onError = (error) => {
      if (error.status === 413) {
        notify(
          "The size of the uploaded photo is large, please try again.",
          "error"
        );
      }
    };

    const croppedFile = await getCropData();
    if (croppedFile) {
      formData.append("file", croppedFile);
      await PUBLIC_REQUEST({
        url: "/upload/",
        method: "post",
        data: formData,
        notifyError: true,
        onSuccess,
        onError,
      });
    }
  };

  // Effect to handle body overflow when modal is shown/hidden
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  // Aspect ratio configurations for different image types
  const types = {
    avatar: {
      aspectRatio: 1 / 1,
      initialAspectRatio: 1 / 1,
      zoomTo: 0.1,
    },
    project: {
      initialAspectRatio: 1 / 1,
      aspectRatio: "auto",
      zoomTo: 0.1,
    },
    header: {
      initialAspectRatio: 6 / 1,
      aspectRatio: 6 / 1,
      zoomTo: 0.1,
    },
  };

  // Function to handle canceling the crop
  const handleCancelCrop = () => {
    setShow(false);
  };

  // JSX for the CropImageModal component
  return (
    <div className="w-full min-h-screen h-screen max-h-screen flex justify-center items-center bg-[#241b4795] backdrop-blur-md fixed top-0 left-0 roboto-font z-50">
      <div
        ref={modalRef}
        className="w-11/12 md:w-[650px] flex flex-col items-center pb-5 pt-1 rounded-3xl  max-h-[800px] bg-white "
      >
        <div className="w-full flex justify-end py-4 px-5">
          <button type="button" onClick={() => setShow(false)}>
            <img src={CloseIcon} alt="CloseIcon" />
          </button>
        </div>
        {/* ////////////////// */}
        <div className="w-full flex flex-col px-5 md:px-10">
          <div className="relative w-full flex justify-center">
            {/* Cropper component for image cropping */}
            <Cropper
              ref={cropperRef}
              style={{ height: 400, width: "100%" }}
              className="bg-[#e1e1e1] rounded-2xl overflow-hidden"
              zoomTo={types[type]["zoomTo"]}
              initialAspectRatio={types[type]["initialAspectRatio"]}
              aspectRatio={types[type]["aspectRatio"]}
              src={image}
              viewMode={1}
              minCropBoxHeight={30}
              minCropBoxWidth={30}
              background={true}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false}
              guides={true}
            />
            {/* File input for selecting a new image */}
            <label className="w-9 h-9 p-1 flex justify-center items-center rounded-full bg-primary absolute right-2 top-2 cursor-pointer">
              <ProfileEdtiIcon />
              <input
                type="file"
                className="hidden"
                onChange={onChange}
                accept={ACCEPT_IMAGE_FILES}
              />
            </label>
          </div>

          {/* //////// */}
          <div className="flex items-center justify-end">
            {/* Button to cancel crop */}
            <button
              type="button"
              onClick={handleCancelCrop}
              className="w-28 mt-7  flex justify-center items-center text-custom-gray-700 hover:text-red-500 py-2.5 rounded-[10px] self-start"
            >
              Cancel
            </button>{" "}
            {/* Button to save cropped image */}
            <button
              type="button"
              onClick={handleUploadImage}
              disabled={uploadImgLoading}
              className="w-28 mt-7   text-white flex justify-center items-center bg-light-primary  py-2.5  rounded-[10px] disabled:opacity-60 "
            >
              {uploadImgLoading ? (
                <Bars
                  height="25"
                  width="25"
                  color="#ffffff"
                  ariaLabel="bars-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                />
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropImageModal;
